import { Box, Container, Typography, Button } from "@mui/material";
import { motion } from "framer-motion";
import React, { useState, useEffect } from "react";
import { servicesData } from "./data";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

// Custom hook to check if the device is mobile
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return isMobile;
};

// ServiceCard Component
const ServiceCard = ({
  backgroundImage,
  logoSrc,
  title,
  heading,
  services,
  isMobile,
  reverse,
  index,
}) => {
  const isLeftCard = index % 2 !== 0;
  const initialX = isMobile ? (isLeftCard ? 50 : -50) : isLeftCard ? 250 : -250;

  return (
    <motion.div
      initial={{ opacity: 0, x: initialX }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, amount: 0.4 }}
      transition={{ duration: 0.7 }}
      whileHover={{
        scale: 1.03,
        opacity: 0.85,
        transition: { duration: 0.1 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: reverse ? "row-reverse" : "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <Box
          sx={{
            flex: "1 1 45%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minWidth: "300px",
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: isMobile ? "300px" : "400px",
              height: isMobile ? "300px" : "400px",
              backgroundImage: `url("${backgroundImage}")`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              borderRadius: "8px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
            }}
          >
            <img
              src={logoSrc}
              alt={title}
              width={100}
              height={100}
              style={{
                position: "relative",
                transform: "translateY(-20px)",
              }}
            />
            {/* Logo Text */}
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 500,
                color: "white",
                textAlign: "center",
                fontSize: { xs: "26px", sm: "36px" },
                maxWidth: "50%",
                lineHeight: "1.2", // Reduces the space between lines
                letterSpacing: "-0.5px",
              }}
            >
              {title}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ flex: "1 1 300px" }}>
          <Typography
            variant="h3"
            sx={{
              fontSize: { xs: "18px", sm: "24px", md: "32px" },
              fontWeight: 700,
              mb: 2,
              lineHeight: { xs: "22px", sm: "30px", md: "40px" },
              color: "#2e3440",
            }}
          >
            {heading}
          </Typography>
          <Box sx={{ mb: 3 }}>
            {services.map((service, index) => (
              <Box
                key={index}
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <Box
                  component="span"
                  sx={{ color: "#a3b18a", fontWeight: "900" }}
                >
                  ✓
                </Box>
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontFamily: "Inter, sans-serif",
                    paddingLeft: "8px",
                  }}
                >
                  {service}
                </Typography>
              </Box>
            ))}
          </Box>
          <Button
            sx={{
              color: "#d9822b",
              fontWeight: 700,
              padding: 0,
              textDecoration: "underline",
              backgroundColor: "transparent",
              "&:hover": {
                backgroundColor: "transparent",
                textDecoration: "underline",
              },
            }}
            variant="text"
          >
            LEARN MORE
          </Button>
        </Box>
      </Box>
    </motion.div>
  );
};

// ServiceSection Component
const ServiceSection = ({ servicesData }) => {
  const isMobile = useIsMobile();

  return (
    <Box sx={{ backgroundColor: "#f4f1ee", py: 0 }}>
      <Container>
        {servicesData.map((service, index) => (
          <Box
            key={index}
            sx={{
              borderRadius: "20px",
              padding: { xs: "20px", md: "40px" },
              marginBottom: "40px",
              backgroundColor: "#f4f1ee",
              display: "flex",
              justifyContent: "center", // Ensures content is centered within the row
              // Apply transform dynamically based on index
              ...(index % 2 === 0 && !isMobile
                ? { transform: "translateX(-35px)" }
                : {}),
              ...(index % 2 !== 0 && !isMobile
                ? { transform: "translateX(60px)" }
                : {}),
              transition: "transform 0.5s ease", // Smooth transition for translate effect
            }}
          >
            <ServiceCard
              backgroundImage={service.backgroundImage}
              logoSrc={service.logoSrc}
              title={service.title}
              heading={service.heading}
              services={service.services}
              isMobile={isMobile}
              reverse={service.reverse}
              index={index}
            />
          </Box>
        ))}
      </Container>
    </Box>
  );
};

// ServicesHeading Component
const ServicesHeading = () => {
  const MotionTypography = motion(Typography);

  return (
    <Box
      component="section"
      py={2}
      sx={{
        backgroundColor: "#f4f1ee",
        marginTop: { xs: "60px", md: "0" },
        width: "100%",
        borderTopRightRadius: "70px",
        borderTopLeftRadius: "70px",
        zIndex: 2,
        position: "relative",
      }}
    >
      <Container>
        <motion.div
          initial={{ opacity: 0, scale: 0 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 1.2 }}
        >
          <Box textAlign="center" py={{ xs: 2, md: 8 }} mt={{ xs: 2, md: 14 }}>
            <MotionTypography
              variant="overline"
              component="h2"
              gutterBottom
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              sx={{
                letterSpacing: 2,
                fontFamily: "Inter, sans-serif",
                fontWeight: 400,
                fontSize: { xs: "14px", md: "20px" },
                lineHeight: { xs: "17px", md: "24px" },
                marginBottom: { xs: 1, md: 6 },
                color: "#7d5a6c",
              }}
            >
              SERVICES
            </MotionTypography>
            <MotionTypography
              variant="h2"
              component="h1"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
              sx={{
                maxWidth: { xs: "100%", sm: "600px", md: "1000px" },
                mx: "auto",
                fontSize: { xs: "20px", sm: "32px", md: "48px" },
                fontWeight: 400,
                lineHeight: { xs: "24px", sm: "38px", md: "58px" },
                fontFamily: "Inter, sans-serif",
                padding: { xs: "0 20px", md: 0 },
                whiteSpace: "pre-line",
                mt: { xs: 0, md: 4 },
                color: "#2e3440",
              }}
            >
              {
                "Gain critical expertise to expand your\n capabilities and grow your bottom line."
              }
            </MotionTypography>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

const ServicesSection = () => {
  return (
    <Box
      sx={{
        backgroundColor: "#f4f1ee",
        borderRadius: "60px",
        overflow: "hidden",
      }}
    >
      <ServicesHeading />
      <ServiceSection servicesData={servicesData} />
    </Box>
  );
};

export default ServicesSection;
